/**
 * 登录页面 — 墨水书卷风
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'
import { useAuthStore } from '../store/authStore'
import './Login.css'

const REMEMBER_KEY = 'crawlbox_remember_login'

// 邮箱图标
const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
)

// 锁图标
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
)

// 眼睛图标（显示密码）
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
)

// 箭头图标
const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
)

// 警告图标
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
)

// 吉祥物 SVG
const Mascot = () => (
  <svg viewBox="0 0 88 88" width="80" height="80">
    <rect x="14" y="20" width="60" height="56" rx="14" fill="#475569" stroke="#1E293B" strokeWidth="2"/>
    <rect x="20" y="6" width="48" height="36" rx="10" fill="#64748B" stroke="#1E293B" strokeWidth="2"/>
    <circle cx="32" cy="22" r="4" fill="#F5F4F0"/>
    <circle cx="56" cy="22" r="4" fill="#F5F4F0"/>
    <circle cx="33" cy="23" r="1.5" fill="#1E293B"/>
    <circle cx="57" cy="23" r="1.5" fill="#1E293B"/>
    <line x1="44" y1="6" x2="44" y2="0" stroke="#1E293B" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="44" cy="0" r="2.5" fill="#B45309"/>
    <rect x="38" y="32" width="12" height="3" rx="1.5" fill="#1E293B"/>
    <rect x="32" y="42" width="24" height="12" rx="2" fill="#1E293B"/>
    <text x="44" y="51" textAnchor="middle" fill="#B45309" fontFamily="ui-monospace, monospace" fontSize="8" fontWeight="700">&gt;_</text>
    <rect x="6" y="36" width="10" height="20" rx="4" fill="#64748B" stroke="#1E293B" strokeWidth="2"/>
    <rect x="72" y="36" width="10" height="20" rx="4" fill="#64748B" stroke="#1E293B" strokeWidth="2"/>
    <rect x="22" y="76" width="18" height="10" rx="3" fill="#1E293B"/>
    <rect x="48" y="76" width="18" height="10" rx="3" fill="#1E293B"/>
  </svg>
)

// 印章组件
const Seal = ({ char, size, fill, label }: { char: string; size: number; fill: string; label: string }) => (
  <div className="seal">
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={size/2 - 2} fill={fill}/>
      <circle cx={size/2} cy={size/2} r={size/2 - 2} fill="none" stroke="#9A3412" strokeWidth="3"/>
      <circle cx={size/2} cy={size/2} r={size/2 - 6} fill="none" stroke="#FFF9F0" strokeWidth="1" opacity="0.4"/>
      <text x={size/2} y={size * 0.68} textAnchor="middle"
            fontFamily="'Songti SC', 'STSong', 'Source Serif Pro', serif"
            fontSize={size * 0.5} fontWeight="700" fill="#FFF9F0">{char}</text>
    </svg>
    <span className="seal-label">{label}</span>
  </div>
)

// 错误处理函数
const getValidationErrorMessage = (errors: any[]): string => {
  if (!errors || errors.length === 0) return '请求参数验证失败'
  const error = errors[0]
  const field = error.loc?.[error.loc.length - 1]
  
  if (field === 'email') {
    if (error.type === 'value_error' || error.msg?.includes('email address')) {
      return '邮箱格式不正确，请输入有效的邮箱地址'
    }
    if (error.type === 'missing') return '请输入邮箱地址'
    return '邮箱格式有误，请检查后重新输入'
  }
  
  if (field === 'password') {
    if (error.type === 'missing') return '请输入密码'
    if (error.type === 'value_error.any_str.min_length' || error.msg?.includes('length')) {
      return '密码长度不能少于6位'
    }
    return '密码格式不正确'
  }
  
  return error.msg || '输入信息格式不正确，请检查后重试'
}

const Login: React.FC = () => {
  const navigate = useNavigate()
  const { login, isAuthenticated, isLoading, clearError } = useAuthStore()
  const [form] = useState({ email: '', password: '', remember: true })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // 读取记住的登录信息
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY)
      if (saved) {
        const { email: savedEmail, password: savedPassword, remember: savedRemember } = JSON.parse(saved)
        setEmail(savedEmail || '')
        setPassword(savedPassword || '')
        setRemember(savedRemember ?? true)
      }
    } catch { /* ignore */ }
  }, [])

  // 如果已登录，跳转到首页
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)
    
    // 简单的客户端验证
    if (!email) {
      setErrorMsg('请输入邮箱地址')
      return
    }
    if (!password) {
      setErrorMsg('请输入密码')
      return
    }

    try {
      await login(email, password)
      
      // 记住密码
      if (remember) {
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({
          email, password, remember: true,
        }))
      } else {
        localStorage.removeItem(REMEMBER_KEY)
      }
      
      message.success('登录成功！')
      navigate('/', { replace: true })
    } catch (error: any) {
      // 显示具体的错误信息
      let errorMessage = '登录失败'
      
      // 处理422验证错误
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors
        if (errors && errors.length > 0) {
          errorMessage = getValidationErrorMessage(errors)
        } else {
          errorMessage = error.response?.data?.message || '请求参数验证失败'
        }
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setErrorMsg(errorMessage)
    }
  }

  const togglePassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className="login-page">
      {/* 左侧品牌面板 */}
      <aside className="brand-panel">
        <div className="top-logo">
          <span className="mark">C</span>
          <span>CrawlBox</span>
        </div>

        <div className="hero">
          <span className="eyebrow">智能知识管理中心</span>
          <h1>把读过的<em>留下来</em>，<br/>把散落的<em>串起来</em>。</h1>
          <p className="lede">
            一座属于你的私人图书馆。收藏文章、管理标签、积累工具库 —— 
            每一篇精选，每一个标签，都在这。
          </p>
        </div>

        {/* 数据卡 */}
        <div className="stats-row">
          <div className="mini-stat kpi-article">
            <div className="ms-label">已收藏文章</div>
            <div className="ms-value">406<span className="unit">篇</span></div>
          </div>
          <div className="mini-stat kpi-time">
            <div className="ms-label">未读阅读时间</div>
            <div className="ms-value">7,291<span className="unit">分钟</span></div>
          </div>
          <div className="mini-stat kpi-tag">
            <div className="ms-label">工具 / 标签</div>
            <div className="ms-value">344<span className="unit">个</span></div>
          </div>
        </div>

        {/* 印章组 */}
        <div className="seal-group">
          <Seal char="藏" size={96} fill="#B45309" label="收藏" />
          <Seal char="类" size={80} fill="#1E293B" label="标签" />
          <Seal char="具" size={72} fill="#0F766E" label="工具" />
        </div>

        {/* 吉祥物 */}
        <div className="mascot">
          <Mascot />
        </div>
      </aside>

      {/* 移动端品牌栏 */}
      <div className="mobile-brand">
        <div className="top-logo">
          <span className="mark">C</span>
          <span>CrawlBox</span>
        </div>
        <div className="m-line">
          把读过的<strong style={{color: 'var(--seal)'}}>留下来</strong>，把散落的<strong style={{color: 'var(--seal)'}}>串起来</strong>。
        </div>
      </div>

      {/* 右侧登录面板 */}
      <main className="login-panel">
        <div className="login-card">
          <h2 className="form-title">欢迎回来</h2>
          <p className="form-sub">登录后继续你的知识之旅</p>

          <form onSubmit={handleSubmit}>
            {/* 错误提示 */}
            {errorMsg && (
              <div className="error-tip">
                <AlertIcon />
                {errorMsg}
              </div>
            )}

            {/* 邮箱字段 */}
            <div className="field">
              <label htmlFor="email">邮箱</label>
              <div className="input-wrap">
                <span className="ico"><MailIcon /></span>
                <input
                  id="email"
                  type="email"
                  placeholder="solming@example.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* 密码字段 */}
            <div className="field">
              <label htmlFor="password">密码</label>
              <div className="input-wrap">
                <span className="ico"><LockIcon /></span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button type="button" className="toggle-pw" onClick={togglePassword} aria-label="显示密码">
                  <EyeIcon />
                </button>
              </div>
            </div>

            {/* 记住我 */}
            <div className="row">
              <label className="check">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                记住我
              </label>
            </div>

            {/* 登录按钮 */}
            <button type="submit" className={`btn-primary ${isLoading ? 'loading' : ''}`} disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  登录中...
                </>
              ) : (
                <>
                  登录
                  <ArrowIcon />
                </>
              )}
            </button>
          </form>

          <p className="signup">
            还没有账号？<a href="/register">立即注册</a>
          </p>
        </div>

        <div className="page-foot">
          © 2026 CrawlBox · 智能知识管理中心
          <span className="ver">v4.0 · 墨水书卷风</span>
        </div>
      </main>
    </div>
  )
}

export default Login
